import React, {useState, useEffect, useRef} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {OfferSubProduct} from '../../../types/offerProduct.type';
import {ArticleInfomation} from '../../../types/ArticleInformation.type';
import {formatPrice} from '../../../utils/react';
import {axiosCaller} from '../../../utils/apiCaller';
import FontAwesome from 'react-fontawesome';
import Tooltip from 'rc-tooltip';
import {toast} from 'react-toastify';
import DiscountedPrice from './DiscountedPrice';

interface Props {
    data: OfferSubProduct;
    index: number;
    positionPrefix: string;
    parentDiscount: number;
    articles: ArticleInfomation[];
    permissions: any;
    readOnly?: boolean;
    onSave: (data: OfferSubProduct) => void;
    onDelete: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
}

const SubProductRow: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string, values?: Record<string, any>) =>
        intl.formatMessage({ id, defaultMessage }, values);

    const [name, setName] = useState<string>(props.data.name || '');
    const [description, setDescription] = useState<string>(props.data.description || '');
    const [articleId, setArticleId] = useState<number | null>(props.data.articleId ?? null);
    const [quantity, setQuantity] = useState<string>(String(props.data.quantity ?? 1));
    const [costPerUnit, setCostPerUnit] = useState<string>(String(props.data.costPerUnit ?? 0));
    const [coefficient, setCoefficient] = useState<string>(String(props.data.coefficient ?? 1));
    const [effectivePrice, setEffectivePrice] = useState<string>(String(props.data.effectivePrice ?? 0));
    const [tax, setTax] = useState<string>(String(props.data.tax ?? 0));
    const [discount, setDiscount] = useState<string>(String(props.data.discount ?? 0));
    const [totalPrice, setTotalPrice] = useState<number>(props.data.totalPrice || 0);
    const [saveLoading, setSaveLoading] = useState<boolean>(false);
    const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
    const [showDesc, setShowDesc] = useState<boolean>(!!props.data.description);
    const [showArticleDropdown, setShowArticleDropdown] = useState<boolean>(false);
    const [articleSearch, setArticleSearch] = useState<string>('');
    const [unitName, setUnitName] = useState<string>(props.data.unitName || '');
    const [units, setUnits] = useState<any[]>([]);
    const [showUnitDropdown, setShowUnitDropdown] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const unitDropdownRef = useRef<HTMLDivElement>(null);

    // Parse helpers — empty string or invalid → 0 for calculation, but input keeps the raw string
    const num = (v: string, fallback: number = 0): number => {
        const n = parseFloat(v);
        return isNaN(n) ? fallback : n;
    };

    // Re-sync local state when server data arrives
    useEffect(() => {
        if (props.data.id) {
            setName(props.data.name || '');
            setDescription(props.data.description || '');
            setArticleId(props.data.articleId ?? null);
            setQuantity(String(props.data.quantity ?? 1));
            setCostPerUnit(String(props.data.costPerUnit ?? 0));
            setCoefficient(String(props.data.coefficient ?? 1));
            setEffectivePrice(String(props.data.effectivePrice ?? 0));
            setTax(String(props.data.tax ?? 0));
            setDiscount(String(props.data.discount ?? 0));
            setTotalPrice(props.data.totalPrice || 0);
            setShowDesc(!!props.data.description);
            setUnitName(props.data.unitName || '');
        }
    }, [props.data.id, props.data.updatedAt]);

    // Resolve unit name from articles list when article is selected
    // Resolve unit name from article's unit relation
    useEffect(() => {
        if (articleId && articleId > 0 && props.articles) {
            const found = props.articles.find((a: any) => a.value === articleId);
            // Only auto-fill from the article when no unit is set yet — don't clobber a
            // saved or manually-picked unit when the articles list loads after the row.
            if (found && !unitName) {
                setUnitName((found as any).unit?.name || '');
            }
        }
    }, [articleId, props.articles]);

    // Auto-calculate total: quantity × cmimi × coefficient × (1 - discount%) × (1 + tax%)
    useEffect(() => {
        const q = num(quantity, 1);
        const cmimi = num(effectivePrice);
        const coeff = num(coefficient, 1) || 1;
        const t = num(tax);
        const d = num(discount);
        const pd = props.parentDiscount || 0;

        const base = q * cmimi * coeff;
        const afterOwnDiscount = base - (base * d / 100);
        const afterParentDiscount = afterOwnDiscount - (afterOwnDiscount * pd / 100);
        const afterTax = afterParentDiscount + (afterParentDiscount * t / 100);
        setTotalPrice(Math.round(afterTax * 100) / 100);
    }, [effectivePrice, quantity, coefficient, tax, discount, props.parentDiscount]);

    // Fetch units for manual selection (when no article)
    useEffect(() => {
        axiosCaller('GET', 'units').then((res) => {
            const data = res?.data?.data || res?.data || [];
            setUnits(data);
        }).catch(() => {});
    }, []);

    // Close article dropdown on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setShowArticleDropdown(false);
            }
        };
        if (showArticleDropdown) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showArticleDropdown]);

    // Close unit dropdown on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (unitDropdownRef.current && !unitDropdownRef.current.contains(e.target as Node)) {
                setShowUnitDropdown(false);
            }
        };
        if (showUnitDropdown) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showUnitDropdown]);

    const handleArticleSelect = (article: any) => {
        setArticleId(article.value);
        setName(article.label || '');
        setUnitName(article.unit?.name || '');
        if (article.costPerUnit != null) {
            setCostPerUnit(String(article.costPerUnit));
        }
        setEffectivePrice(article.pricePerUnit ? String(article.pricePerUnit) : '0');
        setShowArticleDropdown(false);
        setArticleSearch('');
    };

    const filteredArticles = props.articles?.filter((a: any) =>
        !articleSearch || (a.label || '').toLowerCase().includes(articleSearch.toLowerCase())
    ) || [];

    const handleSave = async () => {
        if (!name.trim()) {
            toast.error(fm('offer.subProduct.toast.nameRequired', 'Sub-product name is required!'));
            return;
        }
        if (num(quantity) <= 0) {
            toast.error(fm('offer.subProduct.toast.qtyRequired', 'Quantity must be greater than 0!'));
            return;
        }
        setSaveLoading(true);
        await props.onSave({
            ...props.data,
            name,
            description,
            articleId,
            unitName,
            quantity: num(quantity, 1),
            costPerUnit: num(costPerUnit),
            coefficient: num(coefficient, 1),
            effectivePrice: num(effectivePrice),
            tax: num(tax),
            discount: num(discount),
            totalPrice,
        });
        setSaveLoading(false);
    };

    const canEdit = !props.readOnly && ((!props.permissions.observer) || props.permissions.fullPrivileges);

    return (
        <>
            <div className={`sp-row${!props.data.id ? ' sp-row--unsaved' : ''}`}>
                {/* Position + reorder */}
                <div className="sp-col sp-col-pos">
                    <span className="sp-pos-number">{props.positionPrefix}</span>
                    {canEdit && (props.onMoveUp || props.onMoveDown) && (
                        <span className="sp-reorder">
                            {props.onMoveUp && (
                                <button type="button" className="sp-reorder-btn" onClick={props.onMoveUp}>
                                    <FontAwesome name="chevron-up"/>
                                </button>
                            )}
                            {props.onMoveDown && (
                                <button type="button" className="sp-reorder-btn" onClick={props.onMoveDown}>
                                    <FontAwesome name="chevron-down"/>
                                </button>
                            )}
                        </span>
                    )}
                </div>

                {/* Name — inline input with article dropdown trigger */}
                <div className="sp-col sp-col-name" ref={dropdownRef}>
                    <input
                        type="text"
                        className={`sp-cell-input${props.readOnly ? ' sp-cell-readonly' : ''}`}
                        readOnly={props.readOnly}
                        value={name}
                        onChange={(e) => { setName(e.target.value); if (articleId) setArticleId(null); }}
                        placeholder={fm('offer.subProduct.namePlaceholder', 'Name...')}
                    />
                    {!props.readOnly && (
                        <button
                            type="button"
                            className="sp-article-trigger"
                            onClick={() => setShowArticleDropdown(!showArticleDropdown)}
                            title={fm('offer.subProduct.selectFromArticles', 'Select from articles')}
                        >
                            <FontAwesome name="caret-down"/>
                        </button>
                    )}
                    {showArticleDropdown && (
                        <div className="sp-article-dropdown">
                            <input
                                type="text"
                                className="sp-article-search"
                                value={articleSearch}
                                onChange={(e) => setArticleSearch(e.target.value)}
                                placeholder={fm('offer.subProduct.searchArticle', 'Search article...')}
                                autoFocus
                            />
                            <div className="sp-article-list">
                                {filteredArticles.slice(0, 50).map((a: any) => (
                                    <div
                                        key={a.value}
                                        className={`sp-article-option${articleId === a.value ? ' selected' : ''}`}
                                        onClick={() => handleArticleSelect(a)}
                                    >
                                        {a.label}
                                        {a.costPerUnit != null && (
                                            <span className="sp-article-cost">{a.costPerUnit} €</span>
                                        )}
                                    </div>
                                ))}
                                {filteredArticles.length === 0 && (
                                    <div className="sp-article-empty">
                                        <FormattedMessage id="offer.subProduct.noResults" defaultMessage="No results" />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Njesia (unit) — auto-filled from article, always editable */}
                <div className="sp-col sp-col-type" ref={unitDropdownRef}>
                    {props.readOnly ? (
                        <span className="sp-type-value">{unitName || '—'}</span>
                    ) : (
                        <button
                            type="button"
                            className="sp-unit-trigger"
                            onClick={() => setShowUnitDropdown(!showUnitDropdown)}
                        >
                            {unitName || '—'}
                            <FontAwesome name="caret-down" className="sp-unit-caret"/>
                        </button>
                    )}
                    {showUnitDropdown && (
                        <div className="sp-unit-dropdown">
                            {units.map((u: any) => (
                                <div
                                    key={u.id}
                                    className={`sp-unit-option${unitName === u.name ? ' selected' : ''}`}
                                    onClick={() => { setUnitName(u.name); setShowUnitDropdown(false); }}
                                >
                                    {u.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Coefficient — right after Njesia */}
                <div className="sp-col sp-col-coeff">
                    <input
                        type="text"
                        inputMode="decimal"
                        className={`sp-cell-input${props.readOnly ? ' sp-cell-readonly' : ''}`}
                        readOnly={props.readOnly}
                        value={coefficient}
                        onChange={(e) => setCoefficient(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        placeholder="1.0"
                    />
                </div>

                {/* Qty */}
                <div className="sp-col sp-col-qty">
                    <input
                        type="text"
                        inputMode="decimal"
                        className={`sp-cell-input${props.readOnly ? ' sp-cell-readonly' : ''}`}
                        readOnly={props.readOnly}
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        onFocus={(e) => e.target.select()}
                    />
                </div>

                {/* Cost per unit */}
                <div className="sp-col sp-col-cost">
                    <input
                        type="text"
                        inputMode="decimal"
                        className={`sp-cell-input${props.readOnly ? ' sp-cell-readonly' : ''}`}
                        readOnly={props.readOnly}
                        value={costPerUnit}
                        onChange={(e) => setCostPerUnit(e.target.value)}
                        onFocus={(e) => e.target.select()}
                    />
                </div>

                {/* Effective price (Cmimi) */}
                <div className="sp-col sp-col-price">
                    <input
                        type="text"
                        inputMode="decimal"
                        className={`sp-cell-input${props.readOnly ? ' sp-cell-readonly' : ''}`}
                        readOnly={props.readOnly}
                        value={effectivePrice}
                        onChange={(e) => setEffectivePrice(e.target.value)}
                        onFocus={(e) => e.target.select()}
                    />
                </div>

                {/* Tax */}
                <div className="sp-col sp-col-tax">
                    <input
                        type="text"
                        inputMode="decimal"
                        className={`sp-cell-input${props.readOnly ? ' sp-cell-readonly' : ''}`}
                        readOnly={props.readOnly}
                        value={tax}
                        onChange={(e) => setTax(e.target.value)}
                        onFocus={(e) => e.target.select()}
                    />
                </div>

                {/* Discount */}
                <div className="sp-col sp-col-disc">
                    <input
                        type="text"
                        inputMode="decimal"
                        className={`sp-cell-input${props.readOnly ? ' sp-cell-readonly' : ''}`}
                        readOnly={props.readOnly}
                        value={discount}
                        onChange={(e) => setDiscount(e.target.value)}
                        onFocus={(e) => e.target.select()}
                    />
                </div>

                {/* Total — read only */}
                <div className="sp-col sp-col-total">
                    {props.data.discountPrice != null ? (
                        <DiscountedPrice original={totalPrice} discounted={Number(props.data.discountPrice)} compact />
                    ) : (
                        <span className="sp-total-value">{formatPrice(totalPrice)} €</span>
                    )}
                </div>

                {/* Actions */}
                {canEdit && (
                    <div className="sp-col sp-col-actions">
                        <button
                            type="button"
                            className={`sp-action-btn sp-action-save${saveLoading ? ' loading' : ''}`}
                            onClick={handleSave}
                            disabled={saveLoading}
                        >
                            <FontAwesome name={saveLoading ? 'spinner' : 'save'} spin={saveLoading}/>
                        </button>
                        <Tooltip placement="top" overlay={<span>{fm('common.action.delete', 'Delete')}</span>}>
                            <button
                                type="button"
                                className="sp-action-btn sp-action-delete"
                                onClick={() => setConfirmDelete(true)}
                            >
                                <FontAwesome name="trash"/>
                            </button>
                        </Tooltip>
                        {!showDesc && (
                            <Tooltip placement="top" overlay={<span>{fm('offer.subProduct.descriptionTooltip', 'Description')}</span>}>
                                <button
                                    type="button"
                                    className="sp-action-btn sp-action-desc"
                                    onClick={() => setShowDesc(true)}
                                >
                                    <FontAwesome name="align-left"/>
                                </button>
                            </Tooltip>
                        )}
                    </div>
                )}

                {/* Delete confirmation overlay */}
                {confirmDelete && (
                    <div className="delete-confirm-overlay">
                        <div className="delete-confirm-box">
                            <p className="delete-confirm-title"><FormattedMessage id="offer.deleteConfirm.title" defaultMessage="Are you sure?" /></p>
                            <p className="delete-confirm-msg">
                                <FormattedMessage id="offer.subProduct.deleteConfirmMsg" defaultMessage='Do you want to delete the sub-product "{name}"?' values={{ name }} />
                            </p>
                            <div className="delete-confirm-actions">
                                <button type="button" className="confirm-btn cancel" onClick={() => setConfirmDelete(false)}><FormattedMessage id="common.confirm.cancel" defaultMessage="Cancel" /></button>
                                <button type="button" className="confirm-btn confirm" onClick={() => { setConfirmDelete(false); props.onDelete(); }}><FormattedMessage id="offer.deleteConfirm.confirmDelete" defaultMessage="Delete" /></button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Expandable description row */}
            {showDesc && (
                <div className="sp-desc-row">
                    <div className="sp-col sp-col-pos"></div>
                    <div className="sp-desc-input-wrap">
                        <input
                            type="text"
                            className="sp-cell-input sp-desc-input"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={fm('offer.subProduct.descriptionPlaceholder', 'Description (optional)...')}
                        />
                        <button
                            type="button"
                            className="sp-desc-close"
                            onClick={() => { setShowDesc(false); setDescription(''); }}
                        >
                            <FontAwesome name="times"/>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default SubProductRow;
